import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Building2, ArrowLeft, Menu, X, User, ChevronDown, LogOut, UserCircle } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'

const EmployerLoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, user, logout } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const errorRef = useRef('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }

  // Sync error with ref to prevent loss during re-renders
  useEffect(() => {
    if (errorRef.current && !error) {
      setError(errorRef.current)
    }
  }, [error])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    errorRef.current = ''
    setIsLoading(true)

    if (!email.trim() || !password.trim()) {
      const errorMsg = 'Please fill in all fields'
      setError(errorMsg)
      errorRef.current = errorMsg
      setIsLoading(false)
      return
    }

    try {
      const result = await login(email, password)

      if (result === 'mfa_required') {
        navigate('/verify-otp?portal=employer')
        setIsLoading(false)
        return
      }

      if (!result) {
        const errorMsg = 'Login failed. Please try again.'
        setError(errorMsg)
        errorRef.current = errorMsg
        setIsLoading(false)
        return
      }
      
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Check if user is employer after successful login
      const currentUser = useAuthStore.getState().user
      if (currentUser?.role !== 'employer') {
        const errorMsg = 'Access denied. This account is not registered as an employer. Please use the correct login portal for your role.'
        
        // Show error using toast AND state
        toast.error(errorMsg, { duration: 6000 })
        errorRef.current = errorMsg
        setError(errorMsg)
        setIsLoading(false)
        
        // Sign out in background after showing error
        setTimeout(async () => {
          await supabaseClient.auth.signOut()
          useAuthStore.setState({ user: null, loading: false })
        }, 2000)
        return
      }
      
      // Navigate based on employer approval status
      setIsLoading(false)

      // One-time partnership registration: if metadata says partner but DB profile doesn't reflect it yet
      const { data: { user: supaUser } } = await supabaseClient.auth.getUser()
      const meta = supaUser?.user_metadata
      const epProfile = currentUser.profile as { approval_status?: string; is_industry_partner?: boolean } | undefined

      if (
        meta?.is_industry_partner === true &&
        epProfile?.approval_status === 'approved' &&
        !epProfile?.is_industry_partner &&
        Array.isArray(meta?.partner_departments) &&
        meta.partner_departments.length > 0
      ) {
        await supabaseClient.rpc('register_employer_partnerships', {
          partner_department_names: meta.partner_departments,
        })
        navigate('/employer/pending')
        return
      }

      if (epProfile?.approval_status === 'pending') {
        navigate('/employer/pending')
      } else if (epProfile?.approval_status === 'deactivated') {
        navigate('/employer/deactivated')
      } else {
        navigate('/employer-dashboard')
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Invalid email or password'
      setError(errorMsg)
      errorRef.current = errorMsg
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            <div className="hidden md:flex">
              {user ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Dashboard
                  </button>

                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 focus:outline-none"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                        {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                          <img
                            src={user.profile.profile_picture}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg'
                            }}
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-600" />
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                          <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                        </div>
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false)
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <UserCircle className="w-4 h-4" />
                          <span>View Profile</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Login
                </Link>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</Link>

                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-[11px] text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-center mb-6">
            <Building2 className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
          </div>

          <div className="text-center mb-8">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">Trailblaze</div>
            <p className="text-sm sm:text-base text-gray-600 font-semibold">Employer Login</p>
            <p className="text-xs text-gray-500 mt-1">Access your company recruitment dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Company Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError('')
                }}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="employer@company.com"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setError('')
                  }}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs sm:text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password/employer" className="text-blue-600 hover:underline text-xs sm:text-sm">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center">
            <div className="text-xs sm:text-sm text-gray-600">
              New employer?{' '}
              <Link to="/register/employer" className="text-blue-600 hover:underline font-semibold">
                Register your company
              </Link>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500 mb-3">Looking for a different portal?</p>
            <div className="flex gap-4 text-sm text-center w-full justify-center">
              <Link to="/login" className="text-xs sm:text-sm font-semibold text-blue-600 hover:underline">
                Alumni Login
              </Link>
              <span className="text-gray-400">•</span>
              <Link to="/login/admin" className="text-xs sm:text-sm font-semibold text-blue-600 hover:underline">
                Admin Login
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  )
}

export default EmployerLoginPage
